import { errorMessageIncludes } from "./dayConflicts";

/** Row of `get_official_write_scope` (migration 20261012094253). */
export interface OfficialWriteScopeRow {
  official_id: string;
  is_shared: boolean;
  can_write_person: boolean;
}

export interface OfficialWriteScope {
  officialId: string;
  /** Non-inactive memberships in more than one league (cups excluded). */
  isShared: boolean;
  /** The caller may change identity / contact / RIB / account directly. */
  canWritePerson: boolean;
}

export function mapWriteScopeRows(rows: OfficialWriteScopeRow[] | null | undefined): Map<string, OfficialWriteScope> {
  const map = new Map<string, OfficialWriteScope>();
  for (const r of rows ?? []) {
    map.set(r.official_id, { officialId: r.official_id, isShared: r.is_shared, canWritePerson: r.can_write_person });
  }
  return map;
}

function partitionBy(
  ids: string[],
  scope: Map<string, OfficialWriteScope>,
  canWrite: (s: OfficialWriteScope) => boolean
): { writable: string[]; skipped: string[] } {
  const writable: string[] = [];
  const skipped: string[] = [];
  for (const id of ids) {
    const s = scope.get(id);
    if (s && canWrite(s)) writable.push(id);
    else skipped.push(id);
  }
  return { writable, skipped };
}

/**
 * Ids whose person columns (location, address…) the caller can write now. Unknown ids
 * (out of the caller's leagues) are skipped, and so is every official the person guard
 * would refuse: a shared one the caller does not manage in each league
 * (OFFICIAL_SHARED_REQUIRES_SUPER_ADMIN) or a mono-league one managed elsewhere
 * (OFFICIAL_PERSON_OUT_OF_SCOPE) — `can_write_person` covers both.
 */
export function partitionWritableOfficials(
  ids: string[],
  scope: Map<string, OfficialWriteScope>
): { writable: string[]; skipped: string[] } {
  return partitionBy(ids, scope, (s) => s.canWritePerson);
}

/**
 * Ids whose grade the caller can write. The grade guard (enforce_official_grade_scope_trigger)
 * copies the grade into the caller's memberships of a shared official without failing; it
 * raises OFFICIAL_GRADE_OUT_OF_SCOPE for the whole batch only when the caller manages none
 * of the official's leagues — for a mono-league official, the same test as `can_write_person`.
 */
export function partitionGradeWritableOfficials(
  ids: string[],
  scope: Map<string, OfficialWriteScope>
): { writable: string[]; skipped: string[] } {
  return partitionBy(ids, scope, (s) => s.isShared || s.canWritePerson);
}

export type OfficialEditMode = "direct" | "request";

/**
 * An official whose person the caller cannot write goes through a request, even for its
 * creator: shared (routed to the super-admin by the server) or managed by another league.
 */
export function editModeFor(scope: OfficialWriteScope | undefined, isCreator: boolean): OfficialEditMode {
  if (scope && !scope.canWritePerson) return "request";
  return isCreator ? "direct" : "request";
}

/**
 * Whether the caller may approve / reject a modification request. `requires_super_admin`
 * is stamped when the request is filed; `writeScope` (get_official_write_scope) says
 * whether the official is shared NOW — one that became shared after filing also goes to
 * the super-admin (the RLS UPDATE refuses it too). No scope (RPC not deployed yet:
 * PGRST202 → empty map, or official outside the caller's leagues) = today's rule.
 */
export function canReviewModificationRequest(
  request: { requires_super_admin?: boolean | null },
  superAdmin: boolean,
  writeScope?: OfficialWriteScope
): boolean {
  if (superAdmin) return true;
  if (request.requires_super_admin) return false;
  return !writeScope?.isShared;
}

export function isSharedOfficialWriteError(error: unknown): boolean {
  return (
    errorMessageIncludes(error, "OFFICIAL_SHARED_REQUIRES_SUPER_ADMIN") ||
    errorMessageIncludes(error, "OFFICIAL_ARCHIVE_SUPER_ADMIN_ONLY")
  );
}

export type OfficialWriteErrorKey =
  | "shared.requires_super_admin"
  | "shared.archive_super_admin_only"
  | "shared.person_out_of_scope"
  | "shared.merge_out_of_scope"
  | "shared.grade_out_of_scope"
  | "shared.archive_choose_league"
  | "shared.official_archived";

const WRITE_ERROR_KEYS: Array<[string, OfficialWriteErrorKey]> = [
  ["OFFICIAL_SHARED_REQUIRES_SUPER_ADMIN", "shared.requires_super_admin"],
  ["OFFICIAL_ARCHIVE_SUPER_ADMIN_ONLY", "shared.archive_super_admin_only"],
  ["OFFICIAL_PERSON_OUT_OF_SCOPE", "shared.person_out_of_scope"],
  // merge_officials: the source has rows in a league outside the caller's scope.
  ["OFFICIAL_MERGE_OUT_OF_SCOPE", "shared.merge_out_of_scope"],
  ["OFFICIAL_GRADE_OUT_OF_SCOPE", "shared.grade_out_of_scope"],
  // Client-side (archiveOfficialsInScope): league user with no league selected.
  ["OFFICIAL_ARCHIVE_NO_LEAGUE", "shared.archive_choose_league"],
  // RPC result { success: false, error: 'OFFICIAL_ARCHIVED' } (add / link an archived person).
  ["OFFICIAL_ARCHIVED", "shared.official_archived"],
];

/**
 * `officials:` key of the message for a refused officials write (42501 guards, client
 * codes) or an RPC result `error` code (string), or null: the caller then shows its
 * generic message, never the raw code.
 */
export function officialWriteErrorKey(error: unknown): OfficialWriteErrorKey | null {
  const target = typeof error === "string" ? { message: error } : error;
  for (const [code, key] of WRITE_ERROR_KEYS) {
    if (errorMessageIncludes(target, code)) return key;
  }
  return null;
}

const CODE_SHAPED = /^[A-Z][A-Z0-9_]+$/;

/**
 * Text for an RPC result `error` (add_official_to_league, link_official_to_league…):
 * known code → its translation; any other code-shaped string → the generic message;
 * human-readable text from the RPC → shown as is; nothing → the generic message.
 */
export function resultErrorMessage(
  error: string | null | undefined,
  t: (key: string) => string,
  genericKey: string
): string {
  const key = officialWriteErrorKey(error);
  if (key) return t(key);
  const text = (error ?? "").trim();
  if (!text || CODE_SHAPED.test(text)) return t(genericKey);
  return text;
}

export type AddOfficialSuccessKey =
  | "add_modal.created_success"
  | "add_modal.linked_success"
  | "add_modal.reactivated_success"
  | "add_modal.already_member_success"
  | "add_modal.updated_success";

/** `officials:` key of the success toast for an add / link result `action`. */
export function addOfficialSuccessKey(action: string | null | undefined): AddOfficialSuccessKey {
  switch (action) {
    case "created":
      return "add_modal.created_success";
    case "linked":
      return "add_modal.linked_success";
    case "reactivated":
      return "add_modal.reactivated_success";
    case "already_member":
      return "add_modal.already_member_success";
    default:
      return "add_modal.updated_success";
  }
}

export interface OfficialIdentifierQuery {
  fafPersonId: number | null;
  email: string | null;
  phone: string | null;
}

/** Exact identifiers for `find_official_matches`; null when none is usable. */
export function buildIdentifierQuery(input: { faf: string; email: string; phone: string }): OfficialIdentifierQuery | null {
  const faf = input.faf.trim();
  const email = input.email.trim().toLowerCase();
  const phone = input.phone.replace(/[^0-9+]/g, "");
  const query: OfficialIdentifierQuery = {
    fafPersonId: /^\d{1,12}$/.test(faf) ? Number(faf) : null,
    email: email.includes("@") ? email : null,
    phone: phone.replace(/\+/g, "").length >= 6 ? phone : null,
  };
  return query.fafPersonId !== null || query.email || query.phone ? query : null;
}
